import { CardsThree, Gavel, NotePencil, Pencil, User, X } from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Button, Spinner, Textarea, TextInput, Tooltip } from "flowbite-react";
import {
  Calendar,
  Coins,
  Megaphone,
  TrendDown,
  TrendUp,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import Select, {
  components,
  OptionProps,
  SingleValueProps,
} from "react-select";
import logoIcon from "../../../assets/images/LogoIcon.png";
import AnimatedModal from "../../../components/AnimatedModal";
import { GoogleIcon } from "../../../components/Icons";
import PillSwitch from "../../../components/PillSwitch";
import { BidManagementSkeleton } from "../../../components/SkeletonLoaders/BidManagementSkeleton";
import {
  useCampaignBidData,
  useCampaignBidHistory,
} from "../../../hooks/get/useCampaignBidHistory";
import { httpService } from "../../../services/httpService";
import {
  API_CONFIG,
  BIDDING_STRATEGY_LABEL_MAPPER,
  GOOGLE_CAMPAIGN_STATUS,
  customSelectStyles,
  PERMISSION_MAPPER,
} from "../../../shared/constants";
import { BidHistory, IBidEntry } from "../interface";
import NoCampaignData from "./NoCampaignData";
import { useAuth } from "../../../hooks";
import { checkPermission } from "../../../lib/utils";

// --- Dayjs Setup ---
dayjs.extend(relativeTime);

// --- Types ---
interface CampaignOption {
  value: number;
  label: string;
  campaignType: string;
  googleCampaignId: string;
  campaignId: string;
  currentBid: string;
  lastBid: string;
}

// --- Helper: Campaign Status Badge Classes ---
export function getCampaignStatusBadgeClasses(status: GOOGLE_CAMPAIGN_STATUS): string {
  switch (status) {
    case GOOGLE_CAMPAIGN_STATUS.ENABLED:
      return "bg-green-100 text-green-800 border border-green-300";
    case GOOGLE_CAMPAIGN_STATUS.PAUSED:
      return "bg-yellow-100 text-yellow-800 border border-yellow-300";
    case GOOGLE_CAMPAIGN_STATUS.REMOVED:
      return "bg-red-100 text-red-800 border border-red-300";
    case GOOGLE_CAMPAIGN_STATUS.UNKNOWN:
      return "bg-gray-100 text-gray-800 border border-gray-300";
    case GOOGLE_CAMPAIGN_STATUS.UNSPECIFIED:
    default:
      return "bg-neutral-100 text-neutral-800 border border-neutral-300";
  }
}

// --- Custom Select Components ---
const CustomCampaignOption = ({
  children,
  ...props
}: OptionProps<CampaignOption>) => {
  const campaign = props.data;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-3 w-full">
        <div className="flex items-center justify-center min-w-7 min-h-7 border border-gray-300 rounded-sm flex-shrink-0">
          <Megaphone size={16} className="text-gray-500" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-medium break-words leading-tight">
            {children}
          </span>
          <span className="text-xs text-gray-500 break-words leading-tight">
            {BIDDING_STRATEGY_LABEL_MAPPER[campaign.campaignType]}
          </span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleCampaignValue = ({
  children,
  ...props
}: SingleValueProps<CampaignOption>) => {
  const campaign = props.data;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-3 w-full">
        <div className="flex items-center justify-center min-w-7 min-h-7 border border-gray-300 rounded-sm flex-shrink-0">
          <Megaphone size={16} className="text-gray-500" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-medium break-words leading-tight truncate">
            {children}
          </span>
          <span className="text-xs text-gray-500 break-words leading-tight truncate">
            {BIDDING_STRATEGY_LABEL_MAPPER[campaign.campaignType]}
          </span>
        </div>
      </div>
    </components.SingleValue>
  );
};

// --- Main Component ---
function BidManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // --- State ---
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [newBid, setNewBid] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<GOOGLE_CAMPAIGN_STATUS | null>(
    null
  );
  const [statusError, setStatusError] = useState("");
  // Budget state
  // const [budget, setBudget] = useState("");
  const [originalBudget, setOriginalBudget] = useState("");
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [isBudgetSubmitting, setIsBudgetSubmitting] = useState(false);
  const [budgetError, setBudgetError] = useState("");
  // Add isEditingBid state
  const [isEditingBid, setIsEditingBid] = useState(false);
  // Add bid confirmation modal state
  const [showBidModal, setShowBidModal] = useState(false);
  const [isBidSubmitting, setIsBidSubmitting] = useState(false);
  const [bidError, setBidError] = useState("");

  // --- Params ---
  const { "app-slug": appSlug } = useParams();
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get("country-code");

  // --- Data Fetching ---
  const {
    data: campaignBidData,
    isLoading: isCampaignBidDataLoading,
    refetch: refetchCampaignBidData,
  } = useCampaignBidData({
    appSlug: appSlug as string,
    countryCode: countryCode as string,
  });

  // --- Memoized Data ---
  const selectedEntriesBidHistory: IBidEntry[] = useMemo(() => {
    return campaignBidData?.bid_data
      ? (campaignBidData.bid_data as IBidEntry[])
      : [];
  }, [campaignBidData]);

  const campaignOptions: CampaignOption[] = useMemo(
    () =>
      selectedEntriesBidHistory.map((entry, index) => ({
        value: index,
        label: entry.campaign_name,
        campaignType: entry.campaign_type,
        googleCampaignId: entry.google_campaign_id,
        campaignId: entry.campaign_id.toString(),
        currentBid: entry.current_bid.toString(),
        lastBid: entry.old_bid.toString(),
      })),
    [selectedEntriesBidHistory]
  );

  // --- Selected Bid History State ---
  const [selectedBidHistory, setSelectedBidHistory] = useState<{
    campaignName: string;
    campaignType: string;
    googleCampaignId: string;
    campaignId: string;
    currentBid: string;
    lastBid: string;
    bidHistories: BidHistory[];
    googleCampaignStatus?: GOOGLE_CAMPAIGN_STATUS;
  }>(() => {
    const firstEntry = selectedEntriesBidHistory[0];
    return {
      campaignName: firstEntry?.campaign_name || "",
      campaignType: firstEntry?.campaign_type || "",
      googleCampaignId: firstEntry?.google_campaign_id || "",
      campaignId: firstEntry?.campaign_id?.toString() || "",
      currentBid: firstEntry?.current_bid?.toString() || "",
      lastBid: firstEntry?.old_bid?.toString() || "",
      bidHistories: [],
      googleCampaignStatus: undefined,
    };
  });

  // --- Fetch Bid History for Selected Campaign ---
  const {
    data: campaignBidHistoryData,
    isLoading: isCampaignDataLoading,
    refetch: refetchCampaignBidHistory,
  } = useCampaignBidHistory({
    campaignId: parseInt(selectedBidHistory?.campaignId),
  });

  // --- Effects ---
  // Update selectedBidHistory when campaignBidHistoryData changes
  useEffect(() => {
    if (campaignBidHistoryData) {
      setSelectedBidHistory((prev) => {
        if (
          JSON.stringify(prev?.bidHistories) !==
          JSON.stringify(campaignBidHistoryData.campaign_bid_histories)
        ) {
          return {
            ...prev!,
            bidHistories: campaignBidHistoryData.campaign_bid_histories.slice(
              0,
              5
            ),
            googleCampaignStatus:
              campaignBidHistoryData.google_campaign_status as GOOGLE_CAMPAIGN_STATUS,
          };
        }
        return {
          ...prev,
          googleCampaignStatus:
            campaignBidHistoryData.google_campaign_status as GOOGLE_CAMPAIGN_STATUS,
        };
      });
    }
  }, [campaignBidHistoryData]);

  // Update selected bid history when tab changes
  useEffect(() => {
    const selectedEntry = selectedEntriesBidHistory[activeTab];
    if (selectedEntry) {
      setSelectedBidHistory((prev) => {
        const newCampaignId = selectedEntry.campaign_id.toString();
        if (prev.campaignId !== newCampaignId) {
          return {
            ...prev,
            campaignName: selectedEntry.campaign_name,
            campaignType: selectedEntry.campaign_type,
            googleCampaignId: selectedEntry.google_campaign_id,
            campaignId: newCampaignId,
            currentBid: selectedEntry.current_bid.toString(),
            lastBid: selectedEntry.old_bid.toString(),
            bidHistories: [],
            googleCampaignStatus: undefined,
          };
        }
        return prev;
      });
      setRemarks("");
      setNewBid("");
    }
  }, [activeTab, selectedEntriesBidHistory]);

  // Set initial budget value from selectedEntriesBidHistory
  useEffect(() => {
    const selectedEntry = selectedEntriesBidHistory[activeTab];
    if (selectedEntry) {
      const entryBudget = campaignBidHistoryData?.google_campaign_budget || "";
      // setBudget(entryBudget);
      setOriginalBudget(entryBudget);
      setBudgetInput(entryBudget);
      setIsEditingBudget(false);
      setBudgetError("");
    }
  }, [activeTab, selectedEntriesBidHistory, campaignBidHistoryData]);

  // --- Memoized Last Bid ---
  const lastBid = useMemo(() => {
    const selectedEntry = selectedEntriesBidHistory[activeTab];
    if (!selectedEntry) return null;
    return {
      previous_data:
        selectedEntry.campaign_type === "TARGET_ROAS"
          ? selectedEntry.old_troas
          : selectedEntry.old_bid,
      current_data:
        selectedEntry.campaign_type === "TARGET_ROAS"
          ? selectedEntry.troas
          : selectedEntry.current_bid,
    };
  }, [activeTab, selectedEntriesBidHistory]);

  // --- Get bid and budget values from new BidHistoryResponse fields ---
  const bidType = campaignBidHistoryData?.campaign_bidding_strategy_type;
  const currencySymbol = campaignBidHistoryData?.currency_symbol || "₹";
  const currentBid =
    bidType === "TARGET_ROAS"
      ? campaignBidHistoryData?.campaign_troas
      : campaignBidHistoryData?.campaign_bid;
  const previousBid =
    bidType === "TARGET_ROAS"
      ? campaignBidHistoryData?.campaign_previous_troas
      : campaignBidHistoryData?.campaign_previous_bid;
  const currentBudget = campaignBidHistoryData?.google_campaign_budget;
  const budgetUpdatedBy =
    campaignBidHistoryData?.budget_update_by ||
    campaignBidHistoryData?.status_update_by ||
    "-";
  // const budgetUpdatedOn = campaignBidHistoryData?.budget_update_on;

  // --- Handlers ---
  const handleBidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewBid(e.target.value);
    setError("");
  };

  const handleRemarksChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRemarks(e.target.value);
  };

  const validateBid = (bid: number): boolean => {
    if (isNaN(bid) || bid <= 0) {
      setError(t("common.manageCampaign.errors.invalidBid"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const bidValue = parseFloat(newBid);
    if (!validateBid(bidValue)) return;
    setShowBidModal(true);
  };

  const handleBidModalConfirm = async () => {
    const bidValue = parseFloat(newBid);
    setIsBidSubmitting(true);
    setBidError("");
    const payload = {
      new_bid: bidValue,
      current_bid: lastBid?.current_data,
      google_campaign_id: selectedBidHistory?.googleCampaignId,
      campaign_id: selectedBidHistory?.campaignId,
      remarks,
    };
    try {
      await httpService.post(API_CONFIG.path.updateCampaignBid, payload);
      setShowBidModal(false);
      setIsBidSubmitting(false);
      setIsEditingBid(false);
      refetchCampaignBidData();
      refetchCampaignBidHistory();
      toast.success(t("common.manageCampaign.bidUpdatedSuccessfully"));
    } catch (err: unknown) {
      let message = "Failed to update bid";
      if (
        typeof err === "object" &&
        err !== null &&
        "message" in err &&
        typeof (err as { message: unknown }).message === "string"
      ) {
        message = (err as { message: string }).message;
      }
      setBidError(message);
      setIsBidSubmitting(false);
    }
  };

  const handleBidModalCancel = () => {
    setShowBidModal(false);
    setBidError("");
  };

  // const handleClose = () => navigate("/app-country-reports");

  const handleCampaignChange = (selectedOption: CampaignOption | null) => {
    if (selectedOption) setActiveTab(selectedOption.value);
  };

  const handleStatusSwitch = (value: GOOGLE_CAMPAIGN_STATUS) => {
    if (!selectedBidHistory) return;
    setPendingStatus(value);
    setShowStatusModal(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedBidHistory || !pendingStatus) return;
    setIsStatusSubmitting(true);
    setStatusError("");
    try {
      await httpService.post(API_CONFIG.path.campaignsStatus, {
        campaign_id: selectedBidHistory.campaignId,
        google_campaign_id: selectedBidHistory.googleCampaignId,
        status: pendingStatus,
      });
      setShowStatusModal(false);
      setIsStatusSubmitting(false);
      setSelectedBidHistory((prev) => ({
        ...prev!,
        googleCampaignStatus: pendingStatus,
      }));
    } catch (err: unknown) {
      let message = "Failed to update status";
      if (
        typeof err === "object" &&
        err !== null &&
        "message" in err &&
        typeof (err as { message: unknown }).message === "string"
      ) {
        message = (err as { message: string }).message;
      }
      setStatusError(message);
      setIsStatusSubmitting(false);
    }
  };

  // --- Budget Handlers ---
  const handleEditBudget = () => setIsEditingBudget(true);
  const handleCancelEditBudget = () => {
    setBudgetInput(originalBudget);
    setIsEditingBudget(false);
    setBudgetError("");
  };
  const handleBudgetInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBudgetInput(e.target.value);
    setBudgetError("");
  };
  const handleConfirmEditBudget = () => setShowBudgetModal(true);

  const handleBudgetModalConfirm = async () => {
    setIsBudgetSubmitting(true);
    setBudgetError("");
    try {
      await httpService.post(API_CONFIG.path.updateCampaignBudget, {
        campaign_id: selectedBidHistory?.campaignId,
        google_campaign_id: selectedBidHistory?.googleCampaignId,
        google_campaign_budget_id:
          campaignBidHistoryData?.google_campaign_budget_id,
        google_campaign_budget: parseFloat(budgetInput),
      });
      // setBudget(budgetInput);
      setOriginalBudget(budgetInput);
      setIsEditingBudget(false);
      setShowBudgetModal(false);
      setIsBudgetSubmitting(false);
    } catch (err: unknown) {
      let message = "Failed to update budget";
      if (
        typeof err === "object" &&
        err !== null &&
        "message" in err &&
        typeof (err as { message: unknown }).message === "string"
      ) {
        message = (err as { message: string }).message;
      }
      setBudgetError(message);
      setIsBudgetSubmitting(false);
      setShowBudgetModal(false);
      setBudgetInput(originalBudget);
      setIsEditingBudget(false);
    }
  };

  const handleBudgetModalCancel = () => {
    setShowBudgetModal(false);
    // setBudgetInput(originalBudget);
    // setIsEditingBudget(false);
    setBudgetError("");
  };

  const hasCampaignBidUpdatePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.BID_MANAGEMENT_UPDATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  // --- Render ---
  if (isCampaignBidDataLoading) return <BidManagementSkeleton />;
  if (selectedEntriesBidHistory.length === 0) return <NoCampaignData />;

  return (
    <div className="flex flex-col gap-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Gavel size={20} className="text-gray-700" weight="duotone" />
            <h1 className="text-md sm:text-xl font-bold text-gray-700">
              {t("common.manageCampaign.title")}
            </h1>
          </div>
          {selectedBidHistory?.googleCampaignStatus && (
            <>
              <div className="hidden sm:block w-px bg-gray-200 h-8"></div>
              <div className="flex items-center">
                <PillSwitch
                  disabled={!hasCampaignBidUpdatePermission}
                  options={[
                    {
                      color: "red",
                      label: t("common.manageCampaign.paused"),
                      value: GOOGLE_CAMPAIGN_STATUS.PAUSED,
                    },
                    {
                      color: "green",
                      label: t("common.manageCampaign.enabled"),
                      value: GOOGLE_CAMPAIGN_STATUS.ENABLED,
                    },
                  ]}
                  value={
                    selectedBidHistory?.googleCampaignStatus ||
                    GOOGLE_CAMPAIGN_STATUS.PAUSED
                  }
                  onChange={handleStatusSwitch}
                />
              </div>
            </>
          )}
        </div>
        {/* Campaign Dropdown */}
        {selectedEntriesBidHistory.length > 1 && (
          <div className="w-full sm:w-[350px]">
            <Select<CampaignOption>
              value={campaignOptions[activeTab]}
              onChange={handleCampaignChange}
              menuPlacement="auto"
              classNamePrefix={"custom-select"}
              options={campaignOptions}
              components={{
                Option: CustomCampaignOption,
                SingleValue: SingleCampaignValue,
              }}
              styles={customSelectStyles<CampaignOption>()}
              placeholder={t("common.manageCampaign.selectCampaign")}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-full">
        {isCampaignDataLoading ? (
          <BidManagementSkeleton />
        ) : (
          <div className="bg-white mt-2">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Left Section - Campaign Details & Bid Creation */}
              <div className="w-1/2 px-2">
                <form onSubmit={handleSubmit}>
                  {/* Top Row: Campaign Name | Campaign Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="border border-gray-200 rounded-xl p-3">
                      <label className="block text-sm text-gray-700">
                        {t("common.manageCampaign.campaignName")}
                      </label>
                      <p className="text-md font-semibold text-gray-700 break-words">
                        {campaignBidHistoryData?.campaign_name}
                      </p>
                    </div>
                    <div className="border border-gray-200 rounded-xl p-3">
                      <label className="block text-sm text-gray-700">
                        {t("common.manageCampaign.campaignType")}
                      </label>
                      <p className="text-md font-semibold text-gray-700  truncate">
                        {
                          BIDDING_STRATEGY_LABEL_MAPPER[
                          selectedBidHistory?.campaignType
                          ]
                        }
                      </p>
                    </div>
                  </div>
                  {/* Grouped Cards: Bids and Budget */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left: Bids */}
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-4">
                        {/* Current Bid */}
                        <div>
                          <label className="block text-sm text-gray-700 mr-1">
                            {t("common.manageCampaign.currentBid")}
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-md font-semibold text-gray-700 ">
                              {Number(currentBid).toFixed(2)}
                              <span className="text-md font-semibold text-gray-500 ml-1">
                                {bidType === "TARGET_ROAS"
                                  ? "%"
                                  : currencySymbol}
                              </span>
                            </span>
                            {!isEditingBid &&
                              hasCampaignBidUpdatePermission && (
                                <Tooltip
                                  content={t("common.edit")}
                                  placement="bottom"
                                >
                                  <div
                                    className="cursor-pointer hover:transform"
                                    onClick={() => setIsEditingBid(true)}
                                  >
                                    <Pencil size={20} className="text-gray-700" weight="duotone" />
                                  </div>
                                </Tooltip>
                              )}
                          </div>
                        </div>
                        {/* Previous Bid */}
                        <div>
                          <label className="block text-sm text-gray-700 mr-1">
                            {t("common.manageCampaign.lastBid")}
                          </label>
                          <span className="text-md font-semibold text-gray-700">
                            {Number(previousBid).toFixed(2)}
                            <span className="text-md font-semibold text-gray-500 ml-1">
                              {bidType === "TARGET_ROAS" ? "%" : currencySymbol}
                            </span>
                          </span>
                        </div>
                      </div>
                      {isEditingBid && (
                        <div className="mt-2 space-y-2 animate-fade-in fade-in-5">
                          <TextInput
                            icon={() => (
                              <span className="text-md font-semibold text-gray-500">
                                {bidType === "TARGET_ROAS"
                                  ? "%"
                                  : currencySymbol}
                              </span>
                            )}
                            type="number"
                            value={newBid}
                            onChange={handleBidChange}
                            placeholder={t(
                              "common.manageCampaign.newBidPlaceholder"
                            )}
                            color={error ? "failure" : undefined}
                            helperText={error}
                          />
                          <Textarea
                            value={remarks}
                            onChange={handleRemarksChange}
                            placeholder={t(
                              "common.manageCampaign.remarksPlaceholder"
                            )}
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              color="light"
                              onClick={() => {
                                setIsEditingBid(false);
                                setNewBid("");
                                setRemarks("");
                                setError("");
                              }}
                              className="flex-1"
                            >
                              <X className="size-5 mr-2" />
                              {t("common.manageCampaign.close")}
                            </Button>
                            <Button
                              color="indigo"
                              type="submit"
                              className="flex-1"
                              disabled={!newBid}
                            >
                              <Coins className="w-4 h-4 mr-2" />
                              {t("common.manageCampaign.changeBid")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Budget */}
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between  gap-4">
                        {/* Current Budget */}
                        <div>
                          <label className="block text-sm text-gray-700 mr-1">
                            {t("campaigns.duplicate.form.budget")}
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-md font-semibold text-gray-700">
                              {currentBudget} {currencySymbol}
                            </span>
                            {!isEditingBudget &&
                              hasCampaignBidUpdatePermission && (
                                <Tooltip
                                  content={t("common.edit")}
                                  placement="bottom"
                                >
                                  <div
                                    className="cursor-pointer hover:transform"
                                    onClick={handleEditBudget}
                                  >
                                    <Pencil size={20} className="text-gray-700" weight="duotone" />
                                  </div>
                                </Tooltip>
                              )}
                          </div>
                        </div>
                        {/* Updated By */}
                        <div>
                          <label className="block text-sm text-gray-700 mr-1">
                            {t("common.manageCampaign.budgetUpdatedBy")}
                          </label>
                          <p className="text-md font-semibold text-gray-700">
                            {budgetUpdatedBy}
                          </p>
                          {/* <p className="text-sm text-gray-700">
                            {budgetUpdatedOn && (
                              <>
                                (
                                {dayjs(budgetUpdatedOn).format(
                                  "DD MMM YYYY HH:mm"
                                )}
                                )
                              </>
                            )}
                          </p> */}
                        </div>
                      </div>
                      {isEditingBudget && (
                        <div className="mt-2 space-y-2 animate-fade-in fade-in-5">
                          <TextInput
                            type="number"
                            value={budgetInput}
                            icon={() => (
                              <div className="text-md font-semibold text-gray-500 w-5 flex justify-center items-center">
                                {currencySymbol}
                              </div>
                            )}
                            min={0}
                            onChange={handleBudgetInputChange}
                            placeholder={originalBudget}
                            color={budgetError ? "failure" : undefined}
                          />
                          <div className="flex gap-2">
                            <Button
                              color="light"
                              onClick={handleCancelEditBudget}
                              className="flex-1"
                            >
                              <X className="size-5 mr-2" />
                              {t("common.cancel")}
                            </Button>
                            <Button
                              color="indigo"
                              onClick={handleConfirmEditBudget}
                              className="flex-1"
                              disabled={
                                isBudgetSubmitting ||
                                budgetInput === "0" ||
                                !budgetInput
                              }
                              isProcessing={isBudgetSubmitting}
                              processingSpinner={
                                <Spinner color="purple" size="sm" />
                              }
                            >
                              {t("common.confirm")}
                            </Button>
                          </div>
                        </div>
                      )}
                      {budgetError && (
                        <div className="text-xs text-red-600 mt-1 ml-0.5">
                          {budgetError}
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              {/* Divider */}
              <div className="hidden lg:block w-px bg-gray-200"></div>
              {/* Right Section - History */}
              <div className="w-1/2 border border-gray-200 rounded-xl p-4">
                <p className="block text-sm text-gray-700 mb-2">
                  {t("common.manageCampaign.bidHistory")}
                </p>
                <div className="max-h-[500px] overflow-y-auto">
                  {selectedBidHistory?.bidHistories.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CardsThree
                        size={48}
                        className="mx-auto mb-3 text-gray-300"
                      />
                      <p className="text-sm">
                        {t("common.manageCampaign.noHistoryFound")}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedBidHistory?.bidHistories.map(
                        (history, index) => (
                          <div
                            key={index}
                            className="flex items-start rounded-lg border border-gray-200 py-1 px-2 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex-grow">
                              <div className="flex flex-col items-start justify-between">
                                <div className="flex items-center text-sm gap-4 w-full">
                                  <div className="flex items-center gap-2 min-w-[165px]">
                                    <div className="flex items-center justify-center gap-2">
                                      {/* Icon */}
                                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center">
                                        {history.is_update_by_google ? (
                                          <GoogleIcon size={16} />
                                        ) : (
                                          <img
                                            src={logoIcon}
                                            alt="App Logo"
                                            className="w-4 h-4"
                                          />
                                        )}
                                      </div>
                                      <span className="text-sm font-medium text-gray-900">
                                        {`${history?.type === "TARGET_CPA"
                                            ? history.currency_symbol
                                            : ""
                                          } ${history.current_data} ${history?.type === "TARGET_ROAS"
                                            ? "%"
                                            : ""
                                          }`}
                                      </span>
                                      {history.is_bid_increase ? (
                                        <TrendUp
                                          className="text-green-500"
                                          size={16}
                                        />
                                      ) : (
                                        <TrendDown
                                          className="text-red-500"
                                          size={16}
                                        />
                                      )}
                                      <span className="text-sm text-gray-600">
                                        {`${history?.type === "TARGET_CPA"
                                            ? history.currency_symbol
                                            : ""
                                          } ${history.previous_data} ${history?.type === "TARGET_ROAS"
                                            ? "%"
                                            : ""
                                          }`}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="w-px h-6 bg-gray-300"></div>
                                  <div className="flex items-center gap-2 min-w-[165px]">
                                    <div className="flex items-center justify-center gap-2">
                                      <User
                                        size={16}
                                        className="text-gray-500"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {history.user_name}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="w-px h-6 bg-gray-300"></div>
                                  <div className="flex items-center gap-2 min-w-[165px]">
                                    <div className="flex items-center justify-center gap-2">
                                      <Calendar
                                        size={16}
                                        className="text-gray-500"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {dayjs(history.date).format(
                                          "DD/MM/YYYY h:mm:ss a"
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {history?.remarks && (
                                  <div className="flex items-start justify-start gap-2 mt-1 pl-1.5">
                                    <div className="flex items-start justify-center gap-2">
                                      <NotePencil
                                        size={20}
                                        className="text-gray-500 flex-shrink-0"
                                      />
                                      <p className="text-sm text-gray-600 pl-1.5">
                                        {history.remarks}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Confirmation Modal for Status Change */}
      <AnimatedModal
        show={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={
          pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED
            ? t("common.manageCampaign.confirmEnableTitle")
            : t("common.manageCampaign.confirmPauseTitle")
        }
        isConfirmation
        onConfirm={handleConfirmStatusChange}
        confirmText={
          pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED
            ? t("common.manageCampaign.enable")
            : t("common.manageCampaign.pause")
        }
        cancelText={t("common.cancel")}
        confirmButtonColor={
          pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED ? "green" : "red"
        }
        isConfirming={isStatusSubmitting}
        bodyClassName="text-center"
      >
        <div className="px-4 py-2">
          {pendingStatus === GOOGLE_CAMPAIGN_STATUS.ENABLED
            ? t("common.manageCampaign.confirmEnableMessage")
            : t("common.manageCampaign.confirmPauseMessage")}
          {statusError && (
            <div className="text-red-500 mt-2">{statusError}</div>
          )}
        </div>
      </AnimatedModal>
      {/* Budget Confirmation Modal */}
      <AnimatedModal
        show={showBudgetModal}
        onClose={handleBudgetModalCancel}
        title={t("common.manageCampaign.confirmBudgetEditTitle")}
        isConfirmation
        onConfirm={handleBudgetModalConfirm}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        confirmButtonColor="indigo"
        isConfirming={isBudgetSubmitting}
        bodyClassName="text-center"
      >
        <div className="py-2">
          {t("common.manageCampaign.confirmBudgetEditMessage", {
            value: budgetInput,
          })}
          {budgetError && (
            <div className="text-red-500 mt-2">{budgetError}</div>
          )}
        </div>
      </AnimatedModal>
      {/* Bid Confirmation Modal */}
      <AnimatedModal
        show={showBidModal}
        onClose={handleBidModalCancel}
        title={t("common.manageCampaign.confirmBidEditTitle")}
        isConfirmation
        onConfirm={handleBidModalConfirm}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        confirmButtonColor="indigo"
        isConfirming={isBidSubmitting}
        bodyClassName="text-center p-2"
      >
        <div className="py-2">
          {t("common.manageCampaign.confirmBidEditMessage", {
            value: newBid,
            currency: bidType === "TARGET_ROAS" ? "%" : currencySymbol,
          })}
          {bidError && <div className="text-red-500 mt-2">{bidError}</div>}
        </div>
      </AnimatedModal>
    </div>
  );
}

export default BidManagement;
